import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDescope } from "@descope/react-sdk";
import { Delete } from "@mui/icons-material";
import {
	FormControlLabel, Skeleton,
	Stack, Switch, Typography
} from "@mui/material";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import pages from "../../../constants/pages";
import prettifyDate from "../../../helpers/prettifyDate";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useAuthorization from "../../../hooks/useAuthorization";
import useToastAlert from "../../../hooks/useToastAlert";
import { resetNavigationState } from "../../../redux/navigationSlice";
import {
	selectOrganizationSsoAndScim, selectUser, setFirstName,
	setGroups, setLastName, setOrgId,
	setPermissions, setPhone, setRoles,
	setUserId
} from "../../../redux/userSlice";
import CoAppIconTextButton from "../../global/components/CoAppIconTextButton";
import CoAppMultiSelect from "../../global/components/inputs/CoAppMultiSelect";
import CoAppTextField from "../../global/components/inputs/CoAppTextField";
import CoAppConfirmationModal from "../../global/components/modals/CoAppConfirmationModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import { CoAppActionButton, CoAppCancelTextButton, CoAppProfileAvatar } from "../../global/styled/global.styled";

import { UserProfileNameText } from "./styled/user-profile.styled";

const initialUserDetails = {
	onLoadDetails: {
		active: null,
		firstName: "",
		lastName: "",
		roles: [],
		groups: []
	},
	onEditDetails: {
		active: null,
		firstName: "",
		lastName: "",
		roles: [],
		groups: []
	}
};
export default function UserProfile() {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const isAuthorized = useAuthorization(["MANAGE_USERS_AND_GROUPS"]);
	const isSsoAndScimEnabled = useSelector(selectOrganizationSsoAndScim);
	const user = useSelector(selectUser);
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { logout } = useDescope();

	const [isEditing, setIsEditing] = useState(false);
	const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const [availableGroups, setAvailableGroups] = useState([]);
	const [availableRoles, setAvailableRoles] = useState([]);
	const [email, setEmail] = useState("");
	const [isAuthorizedToEdit, setIsAuthorizedToEdit] = useState(false);
	const [isCoAppSupport, setIsCoAppSupport] = useState(false);
	const [lastHeartbeat, setLastHeartbeat] = useState("");
	const [userCreationDate, setUserCreationDate] = useState("");
	const [userDetails, setUserDetails] = useState(initialUserDetails);
	const [userUpdateSeed, setUserUpdateSeed] = useState(0);
	const [loading, setLoading] = useState(true);

	const getAvatarText = () => {
		if (!email) return null;
		if (userDetails.onLoadDetails.firstName && userDetails.onLoadDetails.lastName) {
			return userDetails.onLoadDetails.firstName[0].toUpperCase() + userDetails.onLoadDetails.lastName[0].toUpperCase();
		} else {
			return email[0].toUpperCase();
		}
	};

	/**
	 * User confirms account de-activation
	 * - Deactive user
	 * - If currently logged in user, log them out, redirect to login page.
	 * - If not currently logged in user, just de-activate user. 
	 */
	const handleDeactivateAccountClick = () => {
		let userJSON = {
			active: userDetails.onLoadDetails.active ? false : true
		};
		axios.put(apiRoutes.setUserActive(id), userJSON, {
			headers: authHeader
		}).then(() => {
			if (user.userId === +id) {
				logout();
				dispatch(setUserId(null));
				dispatch(setEmail(null));
				dispatch(setFirstName(null));
				dispatch(setLastName(null));
				dispatch(setPhone(null));
				dispatch(setRoles(null));
				dispatch(setOrgId(null));
				dispatch(resetNavigationState());
				navigate(pages.login);
			} else {
				initUserProfile();
				handleToggleDisableDialog();
			}
		}).catch((err) => {
			console.log(err);
			handleUpdateUserAlert(messageLevels.ERROR);
		});
	};

	const handleDeleteAccount = () => {
		axios.delete(apiRoutes.deleteUser(id), {
			headers: authHeader
		}).then(() => {
			if (user.userId === +id) {
				logout();
				dispatch(setUserId(null));
				dispatch(setEmail(null));
				dispatch(setFirstName(null));
				dispatch(setLastName(null));
				dispatch(setPhone(null));
				dispatch(setRoles(null));
				dispatch(setOrgId(null));
				dispatch(resetNavigationState());
				navigate(pages.login);
			} else {
				handleToggleDeleteDialog();
				handleToastAlert(messageLevels.INFO, messages.USER_DELETION_SUCCESS_MSG(false));
				navigate(pages.userManagement, { replace: true });
			}
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.USER_DELETION_ERROR_MSG);
		});
	};


	const handleSaveChangesClick = () => {
		let userJSON = {};
		const { onLoadDetails, onEditDetails } = userDetails;

		if (onLoadDetails.firstName !== onEditDetails.firstName) {
			userJSON.firstName = onEditDetails.firstName;
		}
		if (onLoadDetails.lastName !== onEditDetails.lastName) {
			userJSON.lastName = onEditDetails.lastName;
		}
		if (JSON.stringify(onLoadDetails.roles) !== JSON.stringify(onEditDetails.roles)) {
			userJSON.roles = onEditDetails.roles;
		}
		if (JSON.stringify(onLoadDetails.groups) !== JSON.stringify(onEditDetails.groups)) {
			userJSON.groups = onEditDetails.groups;
		}

		if (Object.keys(userJSON).length > 0) {
			axios.put(apiRoutes.updateUser + "/" + id, userJSON, {
				headers: authHeader
			}).then((res) => {
				if (user.userId === +id) {
					dispatch(setFirstName(res.data.firstname));
					dispatch(setLastName(res.data.lastname));
					dispatch(setGroups(res.data.groups));
					dispatch(setRoles(res.data.roles));
					dispatch(setPermissions(res.data.permissions));
				}
				setIsEditing(false);
				handleToastAlert(messageLevels.SUCCESS, messages.USER_UPDATE_SUCCESS_MSG);
			}).catch((err) => {
				handleUpdateUserAlert(messageLevels.ERROR);
				setUserDetails({ ...userDetails, onEditDetails: userDetails.onLoadDetails });
				console.log(err);
				return null;
			});
		}
	};

	const handleUpdateUserAlert = (level) => {
		setUserUpdateSeed(Math.random());
		setIsEditing(false);
		handleToastAlert(level, level === "success" ? messages.USER_UPDATE_SUCCESS_MSG : messages.USER_UPDATE_ERROR_MSG);
	};

	const handleToggleDisableDialog = () => {
		setIsDisableDialogOpen(!isDisableDialogOpen);
	};

	const handleToggleDeleteDialog = () => {
		setIsDeleteDialogOpen(!isDeleteDialogOpen);
	};

	const handleMultiselectChange = (e, value, field) => {
		const newDetails = { ...userDetails, onEditDetails: { ...userDetails.onEditDetails, [field]: value } };
		if (userDetailsHaveChanged(newDetails)) {
			setIsEditing(true);
		} else {
			setIsEditing(false);
		}
		setUserDetails(newDetails);
	};

	const handleValueChange = (e, field) => {
		const newDetails = { ...userDetails, onEditDetails: { ...userDetails.onEditDetails, [e.target.id !== undefined ? e.target.id : field]: e.target.value } };
		if (userDetailsHaveChanged(newDetails)) {
			setIsEditing(true);
		} else {
			setIsEditing(false);
		}
		setUserDetails(newDetails);
	};

	const handleCancelButtonClick = () => {
		setUserDetails({ ...userDetails, onEditDetails: userDetails.onLoadDetails });
		setIsEditing(false);
	};

	const initUserProfile = async () => {
		try {
			const isSelf = user && user.userId ? (user.userId === +id ? 1 : 0) : 0;
			const response = await axios.get(apiRoutes.getUser(isSelf, id, ""), { headers: authHeader });
			if (response.data) {
				let details = {
					onLoadDetails: {
						active: response.data.active || false,
						firstName: response.data.firstname || "",
						lastName: response.data.lastname || "",
						roles: response.data.roles || [],
						groups: response.data.groups ? response.data.groups.map(g => g.name) : [],
					},
					onEditDetails: {
						active: response.data.active || false,
						firstName: response.data.firstname || "",
						lastName: response.data.lastname || "",
						roles: response.data.roles || [],
						groups: response.data.groups ? response.data.groups.map(g => g.name) : [],
					}
				};
				setUserDetails(details);
				setEmail(response.data.email || "");
				setLoading(false);
				setLastHeartbeat(response.data.lastHeartbeat === null ? "Never" : prettifyDate(response.data.lastHeartbeat));
				setUserCreationDate(prettifyDate(response.data.createdAt));
				setIsCoAppSupport(response.data.isCoAppSupport);
				let isCoAppSupport = response.data.isCoAppSupport;

				if (isAuthorized && !isCoAppSupport) {
					setIsAuthorizedToEdit(true);
				} else {
					setIsAuthorizedToEdit(false);
				}

				await initRoleAndGroupOptions();
			}
		} catch (err) {
			console.log(err);
		}
	};
	
	const initRoleAndGroupOptions = async () => {
		try {
			const [groupsResponse, rolesResponse] = await Promise.all([
				axios.get(apiRoutes.getGroups, { 
					headers: authHeader,
				}),
				axios.get(apiRoutes.getRoles, { 
					headers: authHeader,
				})
			]);
			
			if (groupsResponse.data) {
				setAvailableGroups(groupsResponse.data);
			}
			
			if (rolesResponse.data) {
				setAvailableRoles(rolesResponse.data);
			}
		} catch (err) {
			console.log(err);
		}
	};

	const userDetailsHaveChanged = (details) => {
		let temp = {
			...details,
			onEditDetails: {
				...details.onEditDetails,
				roles: [...details.onEditDetails.roles].sort(),
				groups: [...details.onEditDetails.groups].sort()
			},
			onLoadDetails: {
				...details.onLoadDetails,
				roles: [...details.onLoadDetails.roles].sort(),
				groups: [...details.onLoadDetails.groups].sort()
			}
		};
		return JSON.stringify(temp.onLoadDetails) !== JSON.stringify(temp.onEditDetails);
	};

	useEffect(() => {
		initUserProfile();
	}, [id, userUpdateSeed]);

	return (
		<>
			<Stack sx={{ padding: "40px" }} spacing={5}>
				<Stack direction="row" spacing={1} justifyContent="space-between">
					<Stack direction="row" spacing={1}>
						{
							loading ?
								<>
									<Skeleton variant="circular" width={60} height={60} />
									<Skeleton variant="rectangular" width={200} height={60} sx={{ borderRadius: "10px" }} />
								</>
								:
								<>
									<CoAppProfileAvatar h="60px" w="60px" fs="28px">
										{getAvatarText()}
									</CoAppProfileAvatar>
									<UserProfileNameText>
										{userDetails.onLoadDetails.firstName && userDetails.onLoadDetails.lastName ? `${userDetails.onLoadDetails.firstName} ${userDetails.onLoadDetails.lastName}` : null}
									</UserProfileNameText>
								</>
						}
					</Stack>
					{
						loading ?
							<Skeleton variant="rectangular" width={125} height={30} sx={{ borderRadius: "10px", margin: "40px 20px 20px 10px" }} />
							:
							<FormControlLabel
								sx={{ marginRight: "10px" }}
								labelPlacement="start"
								label={<Typography variant="body1">{userDetails.onLoadDetails.active ? "Active" : "Inactive"}</Typography>}
								control={
									<Switch
										checked={userDetails.onLoadDetails.active}
										onClick={handleToggleDisableDialog}
										disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isCoAppSupport)}
									/>
								}
							/>
					}
				</Stack>
				<Stack direction="row" spacing={1} justifyContent="space-between">
					<Stack direction="column" spacing={10} sx={{paddingRight: "25px"}}>	
						<Stack direction="row" spacing={8}>
							{
								loading ?
									<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "10px", marginTop: "30px", width: { xxl: "250px", xl: "220px", lg: "170px", md: "150px" } }} />
									:
									<CoAppTextField
										sx={{ width: { xxl: "250px", xl: "220px", lg: "170px", md: "150px" } }}
										id="firstName"
										label="First Name"
										onChange={handleValueChange}
										value={userDetails.onEditDetails.firstName}
										variant="standard"
										disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isCoAppSupport)}
									/>
							}
							{
								loading ?
									<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "10px", width: { xxl: "250px", xl: "220px", lg: "170px", md: "150px" } }} />
									:
									<CoAppTextField
										sx={{ width: { xxl: "250px", xl: "220px", lg: "170px", md: "150px" } }}
										id="lastName"
										variant="standard"
										label="Last Name"
										value={userDetails.onEditDetails.lastName}
										onChange={handleValueChange}
										disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isCoAppSupport)}
									/>
							}
						</Stack>
						{
							loading ?
								<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "10px", marginTop: "30px", width: { xxl: "565px", xl: "565px", lg: "420px", md: "380px" } }} />
								:
								<CoAppMultiSelect
									disabled={!isAuthorizedToEdit || (isSsoAndScimEnabled && !isCoAppSupport)}
									label="Roles"
									options={availableRoles.map(role => role.name)}
									value={userDetails.onEditDetails.roles}
									onChange={(e, value) => handleMultiselectChange(e, value, "roles")}
								/>
						}
					</Stack>
					<Stack direction="column" spacing={10} sx={{paddingRight: "35px"}}>	
						{
							loading ?
								<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "10px", marginTop: "30px", width: { xxl: "565px", xl: "565px", lg: "420px", md: "380px" } }} />
								:
								<CoAppTextField
									sx={{ width: { xxl: "565px", xl: "565px", lg: "420px", md: "380px" } }}
									disabled={true}
									id="email"
									label="Email"
									isemail="true"
									value={email}
									variant="standard"
								/>
						}
						{
							loading ?
								<Skeleton variant="rectangular" height={40} sx={{ borderRadius: "10px", marginTop: "30px", width: { xxl: "565px", xl: "565px", lg: "420px", md: "380px" } }} />
								:
								<CoAppMultiSelect
									disabled={!isAuthorizedToEdit || (!isSsoAndScimEnabled && !isCoAppSupport)}
									label="Groups"
									options={availableGroups.map(role => role.name)}
									value={userDetails.onEditDetails.groups}
									onChange={(e, value) => handleMultiselectChange(e, value, "groups")}
								/>
						}
					</Stack>
				</Stack>
				<Stack direction="row" spacing={1} justifyContent="space-between">
					<Stack direction="row" spacing={{xxl: 45, xl: 30, lg: 20, md: 10}}>
						{
							loading ?
								<Skeleton variant="rectangular" height={30} sx={{ borderRadius: "10px", width: { xxl: "200px", xl: "170px", lg: "120px", md: "100px" } }} />
								:
								<CoAppIconTextButton
									icon={<Delete fontSize="small" sx={{ marginRight: "5px" }} />}
									text="Delete account"
									onClick={handleToggleDeleteDialog}
									isDestructive={true}
								/>
						}
						{
							loading ?
								<>
									<Skeleton variant="rectangular" height={30} sx={{ borderRadius: "10px", width: { xxl: "250px", xl: "220px", lg: "170px", md: "150px" } }} />
									<Skeleton variant="rectangular" height={30} sx={{ borderRadius: "10px", width: { xxl: "200px", xl: "170px", lg: "120px", md: "100px" } }} />
								</>
								:
								<div style={{ display: "flex", flexDirection: "row", gap: "20px", marginTop: "10px" }}>
									<Typography variant="body2" color="text.secondary">Last heartbeat: {lastHeartbeat.toString().replace(",", " at ")}</Typography>
									<Typography variant="body2" color="text.secondary">Created: {userCreationDate.toString().replace(",", " at ")}</Typography>
								</div>
						}
					</Stack>							
					<Stack direction="row" spacing={1} justifyContent="flex-end">
						<CoAppCancelTextButton onClick={handleCancelButtonClick} sx={{display: isEditing ? "flex" : "none"}}>Cancel</CoAppCancelTextButton>
						<CoAppActionButton onClick={handleSaveChangesClick} sx={{display: isEditing ? "flex" : "none"}}>Save</CoAppActionButton>
					</Stack>
				</Stack>
			</Stack>
			<CoAppDestructiveConfirmationModal
				dialogOpen={isDisableDialogOpen && userDetails.onLoadDetails.active}
				dialogMessage={`Are you sure you want to disable ${email}? The user will no longer be able to log in once disabled.`}
				dialogTitle={`Disable ${email}`}
				confirmClickHandler={handleDeactivateAccountClick}
				cancelClickHandler={handleToggleDisableDialog}
				actionText="Disable"
			/>
			<CoAppConfirmationModal
				dialogOpen={isDisableDialogOpen && !userDetails.onLoadDetails.active}
				dialogMessage={`Are you sure you want to enable ${email}? The user will be able to log in once enabled.`}
				dialogTitle={`Enable ${email}`}
				confirmClickHandler={handleDeactivateAccountClick}
				cancelClickHandler={handleToggleDisableDialog}
				actionText="Enable"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={isDeleteDialogOpen}
				dialogMessage={`Are you sure you want to delete ${email}? The user will no longer exist in your organization once deleted.`}
				dialogTitle={`Delete ${email}`}
				confirmClickHandler={handleDeleteAccount}
				cancelClickHandler={handleToggleDeleteDialog}
				actionText="Delete"
			/>
		</>
	);
}